import type { Locale } from "@/lib/i18n";

export interface Heading {
  level: 2 | 3;
  text: string;
  slug: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

/** Extract H2/H3 headings from a markdown body, ignoring code fences. */
export function extractHeadings(body: string): Heading[] {
  if (!body) return [];
  const lines = body.split("\n");
  const out: Heading[] = [];
  let inFence = false;
  for (const raw of lines) {
    const line = raw.replace(/\r$/, "");
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = line.match(/^(#{2,3})\s+(.+?)\s*#*\s*$/);
    if (!m) continue;
    const level = m[1].length === 2 ? 2 : 3;
    const text = m[2].trim();
    out.push({ level: level as 2 | 3, text, slug: slugify(text) });
  }
  return out;
}

const FAQ_HEADERS = [
  "faq",
  "faqs",
  "frequently asked questions",
  "preguntas frecuentes",
  "preguntas comunes",
];

export interface FAQItem {
  question: string;
  answer: string;
}

/** Parse a FAQ section (## FAQ / ## Preguntas Frecuentes) into Q/A pairs. */
export function extractFAQs(body: string): FAQItem[] {
  if (!body) return [];
  const lines = body.split("\n");
  let i = 0;
  let start = -1;
  for (; i < lines.length; i++) {
    const m = lines[i].match(/^##\s+(.+?)\s*$/);
    if (m && FAQ_HEADERS.includes(m[1].trim().toLowerCase())) {
      start = i + 1;
      break;
    }
  }
  if (start < 0) return [];

  const out: FAQItem[] = [];
  let q: string | null = null;
  let buf: string[] = [];
  for (let j = start; j < lines.length; j++) {
    const line = lines[j];
    if (/^##\s/.test(line)) break;
    const h3 = line.match(/^###\s+(.+?)\s*$/);
    if (h3) {
      if (q) out.push({ question: q, answer: buf.join("\n").trim() });
      q = h3[1].trim();
      buf = [];
    } else if (q) {
      buf.push(line);
    }
  }
  if (q) out.push({ question: q, answer: buf.join("\n").trim() });
  return out.filter((f) => f.question && f.answer);
}

export function faqJsonLdFromBody(body: string) {
  const items = extractFAQs(body);
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer.replace(/\n+/g, " ").slice(0, 1000),
      },
    })),
  };
}

export function formatUpdated(iso: string, locale: Locale): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    // Deterministic formatter, Intl.DateTimeFormat output can differ between
    // workerd (SSR) and the browser, which triggers React #418 hydration errors.
    const MONTHS_EN = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December",
    ];
    const MONTHS_ES = [
      "enero","febrero","marzo","abril","mayo","junio",
      "julio","agosto","septiembre","octubre","noviembre","diciembre",
    ];
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth();
    const day = d.getUTCDate();
    return locale === "es"
      ? `${day} de ${MONTHS_ES[m]} de ${y}`
      : `${MONTHS_EN[m]} ${day}, ${y}`;
  } catch {
    return "";
  }
}

/**
 * Per-slug HowTo schema steps. Returns null when the slug has no HowTo schema.
 * Steps mirror the H2 phases in the article body 1:1.
 */
export function howToStepsForSlug(
  slug: string,
  locale: Locale,
): { name: string; description: string; steps: { name: string; text: string }[] } | null {
  if (slug === "shopify-migration-timeline") {
    if (locale === "es") {
      return {
        name: "Cómo migrar una marca DTC establecida a Shopify",
        description:
          "Cronograma de 9 semanas en 5 fases para replatformar una tienda DTC establecida a Shopify o Shopify Plus sin perder tráfico orgánico, suscripciones ni integraciones.",
        steps: [
          {
            name: "Discovery y auditoría de datos",
            text: "Inventariar cada integración (ERP, 3PL, ESP, suscripciones, loyalty, CDP), exportar y limpiar el catálogo (productos, variantes, metafields, redirects, cuentas), mapear cada URL del sitio actual y decidir entre Shopify y Shopify Plus.",
          },
          {
            name: "Theme y checkout",
            text: "Construir sobre un theme Online Store 2.0 moderno o headless si el equipo lo soporta. Reconstruir PDP, PLP, carrito y flujos de cuenta. Configurar checkout extensibility en Plus para cualquier lógica custom.",
          },
          {
            name: "Integraciones e importación",
            text: "Conectar ERP/3PL vía Shopify Flow, conectores nativos o middleware. Importar histórico de pedidos para continuidad del cliente. Migrar suscripciones limpiamente con un plan de comunicación al cliente probado.",
          },
          {
            name: "QA, redirects y soft launch",
            text: "301 a cada URL legada, cutover de staging con tráfico real, paridad de píxeles entre GA4, Meta CAPI, TikTok y Klaviyo.",
          },
          {
            name: "Cutover y estabilización",
            text: "Cambio de DNS en la ventana de menor tráfico. Monitorear Search Console, tasa de conversión y AOV durante 30 días; recuperación completa en 4–6 semanas si redirects y schema están bien hechos.",
          },
        ],
      };
    }
    return {
      name: "How to migrate an established DTC brand to Shopify",
      description:
        "Five-phase, nine-week timeline to replatform an established DTC brand onto Shopify or Shopify Plus without losing organic traffic, subscriptions, or integrations.",
      steps: [
        {
          name: "Discovery and data audit",
          text: "Inventory every integration (ERP, 3PL, ESP, subscriptions, loyalty, CDP), export and clean catalog data (products, variants, metafields, redirects, customer accounts), map every URL on the current site, and decide between Shopify and Shopify Plus.",
        },
        {
          name: "Theme and checkout build",
          text: "Build on a modern Online Store 2.0 theme or headless if the team can support it. Rebuild PDP, PLP, cart, and account flows. Configure checkout extensibility on Plus for any custom logic.",
        },
        {
          name: "Integrations and data import",
          text: "Wire ERP/3PL through Shopify Flow, native connectors, or middleware. Import historical orders for customer continuity. Migrate subscriptions cleanly with a tested customer-comm plan.",
        },
        {
          name: "QA, redirects, soft launch",
          text: "301 every legacy URL, run a staging cutover with a real subset of traffic, ensure pixel parity across GA4, Meta CAPI, TikTok, and Klaviyo.",
        },
        {
          name: "Cutover and stabilization",
          text: "DNS swap during the lowest-traffic window. Monitor Search Console, conversion rate, and AOV for 30 days; full recovery in 4–6 weeks if redirects and schema were done right.",
        },
      ],
    };
  }
  return null;
}